/**
 * @flow
 * @format
 */

import React from 'react';
import cc from 'classcat';
import _ from 'lodash';
import * as d3 from 'd3';
import FaCircle from 'react-icons/lib/fa/circle';

import { placeRound, midAngle, toPercent } from '~/utils';

// import debug from '~/utils/macros/debug.macro';

import classes from './PieChart.scss';

type PropsType = {
  data: { [key: string]: number },
  radius: number,
  totalLoss: number,
  viewBox: {|
    height: number,
    width: number,
    x: number,
    y: number,
  |},
};

type DataPointType = { key: string, val: number };
type ArcType = {
  data: DataPointType,
  endAngle: number,
  startAngle: number,
  value: number,
};

type LegendType = {
  color: string,
  name: string,
  percent: number,
  value: number,
};

const SCALER = 12;

export class PieChart extends React.PureComponent<PropsType, *> {
  static defaultProps = {};
  props: PropsType;

  render() {
    const { data, radius, viewBox, totalLoss, ...rest } = this.props;
    const mappedData = _.map(data, (v, k) => ({ val: v, key: k }));

    const arcs = d3
      .pie()
      .value(d => placeRound(d.val))
      .sort(null)(mappedData);
    const arcGen = d3
      .arc()
      .innerRadius(0)
      .outerRadius(radius * 0.8);

    const outerArc = d3
      .arc()
      .innerRadius(radius * 0.6)
      .outerRadius(radius * 0.7);

    const color = d3.scaleOrdinal(d3.schemeCategory10);
    const pieArcs = arcs.map((a: ArcType, i: number): {} => {
      const ratio = Math.abs(a.startAngle - a.endAngle) / 2 / Math.PI;
      const mid = midAngle(a.startAngle, a.endAngle);
      const posModifier = mid < Math.PI ? 1 : -1;

      const center: number[] = outerArc.centroid(a);
      const pos: number[] = [radius * posModifier, center[1]];
      const percentCalc = placeRound(toPercent(a.data.val, totalLoss), 1);

      const label: React$Element<*> = (
        <text
          className={classes.percentText}
          transform={`translate(${pos[0]}, ${pos[1] - i * SCALER})`}
          textAnchor={pos[0] < 0 ? 'end' : 'start'}
          dy="0.35em"
          fill="black"
        >
          {percentCalc}%
        </text>
      );

      const fillColor: string = color(ratio);
      return {
        data: (
          <g key={`arc${a.value}`} className="slice">
            <path fill={fillColor} stroke="black" d={arcGen(a)} />
            {percentCalc >= 0.5 ? (
              <g>
                {label}
                <polyline
                  points={[
                    arcGen.centroid(a),
                    [center[0], center[1] - i * SCALER],
                    [radius * 0.95 * posModifier, center[1] - i * SCALER],
                  ]}
                  fill="none"
                  stroke="black"
                  style={{
                    opacity: 0.3,
                    strokeWidth: '2px',
                  }}
                />
              </g>
            ) : null}
          </g>
        ),
        legend: {
          name: _.startCase(a.data.key),
          color: fillColor,
          value: placeRound(a.data.val, 3),
          percent: percentCalc,
        },
      };
    });

    const legendRows = pieArcs.map(
      (d: { data: [], legend: LegendType, percent: number, value: number }) => (
        <li key={d.legend.name}>
          <FaCircle color={d.legend.color} /> {d.legend.name}: {d.legend.value}{' '}
          ({d.legend.percent}%)
        </li>
      ),
    );

    return (
      <div>
        <svg
          {...rest}
          viewBox={`${viewBox.x} ${viewBox.y} ${viewBox.width} ${
            viewBox.height
          }`}
        >
          <g
            transform={`translate(${viewBox.width / 2}, ${viewBox.height / 2})`}
          >
            {pieArcs.map(i => i.data)}
          </g>
        </svg>
        <ul
          className={cc([classes.legend, 'list-unstyled'])}
          style={{
            textAlign: 'left',
            transform: 'translateX(-50%)',
            width: '50%',
            marginLeft: '50%',
          }}
        >
          {legendRows}
          <li>Total Power Loss (W): {placeRound(totalLoss, 3)}</li>
        </ul>
      </div>
    );
  }
}

export default PieChart;



// WEBPACK FOOTER //
// ./src/components/PieChart/PieChart.js